import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

type Accent = "teal" | "lav" | "rose" | "mint" | "peach" | "sky";

interface MegaItem {
  title: string;
  blurb: string;
  to?: string;
  href?: string;
  external?: boolean;
  badge?: string;
  accent: Accent;
}

interface MegaSection {
  heading: string;
  items: MegaItem[];
}

interface MegaMenu {
  label: string;
  sections: MegaSection[];
  cta?: { to: string; label: string; blurb: string };
}

const ABOUT_MENU: MegaMenu = {
  label: "About",
  sections: [
    {
      heading: "Sanctology",
      items: [
        {
          title: "Our mission",
          blurb: "Who we serve, what we believe, and where we work.",
          to: "/about",
          accent: "lav",
        },
        {
          title: "How we're funded",
          blurb:
            "Powered by profits from our sibling Logy companies — no public fundraising.",
          to: "/support",
          accent: "teal",
        },
      ],
    },
    {
      heading: "Logy family",
      items: [
        {
          title: "Chaosology",
          blurb: "Future-Driven Technology, Today's Solution.",
          href: "https://chaosology.com",
          external: true,
          accent: "sky",
        },
        {
          title: "Boreology",
          blurb: "Covenant Candles & Hosts — premium church supplies.",
          href: "https://boreology.com",
          external: true,
          accent: "peach",
        },
        {
          title: "Lectureology",
          blurb: "UK diplomas, K–12 tuition, and test prep — globally.",
          href: "https://lectureology.com",
          external: true,
          accent: "mint",
        },
      ],
    },
  ],
  cta: {
    to: "/support",
    label: "The funding model →",
    blurb: "Why we never ask the people we serve — or the public — for money.",
  },
};

// Top-level tabs (direct links — no dropdown)
const DIRECT_LINKS = [
  {
    to: "/elder-sanctuary",
    label: "Elder Sanctuary",
    sub: "Homeless elders",
  },
  {
    to: "/orphan-sanctuary",
    label: "Orphan Sanctuary",
    sub: "Orphan children",
  },
  { to: "/volunteer", label: "Volunteer" },
  { to: "/contact", label: "Contact" },
];

function AccentDot({ accent }: { accent: Accent }) {
  const colors: Record<Accent, string> = {
    teal: "bg-teal-400",
    lav: "bg-lav-glow",
    rose: "bg-rose-glow",
    mint: "bg-mint-glow",
    peach: "bg-peach-glow",
    sky: "bg-sky-glow",
  };
  return (
    <span
      className={`inline-block h-2 w-2 rounded-full ${colors[accent]} ring-4 ring-white shadow-sm`}
    />
  );
}

function MegaPanel({ menu }: { menu: MegaMenu }) {
  return (
    <div className="absolute left-1/2 top-full pt-3 -translate-x-1/2 w-[min(calc(100vw-2rem),820px)] z-50">
      <div
        className="rounded-3xl p-6 sm:p-8 animate-fadeUp border border-white"
        style={{
          background: "rgba(255,255,255,0.97)",
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
          boxShadow:
            "0 40px 100px -32px rgba(91,63,228,0.32), 0 14px 36px -18px rgba(11,13,35,0.2)",
        }}
      >
        <div className="grid gap-6 sm:grid-cols-2">
          {menu.sections.map((s) => (
            <div key={s.heading}>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-500 mb-3">
                {s.heading}
              </div>
              <div className="space-y-1.5">
                {s.items.map((item) => {
                  const content = (
                    <div className="group rounded-xl px-3 py-2.5 -mx-3 hover:bg-white/80 transition flex gap-3 items-start">
                      <span className="mt-1.5">
                        <AccentDot accent={item.accent} />
                      </span>
                      <span className="flex-1">
                        <span className="flex items-center gap-2">
                          <span className="font-semibold text-ink-900">
                            {item.title}
                          </span>
                          {item.badge && (
                            <span className="font-mono text-[9px] uppercase tracking-widest rounded-full bg-mint-100 text-mint-glow px-2 py-0.5">
                              {item.badge}
                            </span>
                          )}
                          {item.external && (
                            <svg
                              className="w-3 h-3 text-ink-400"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M7 17L17 7M17 7H9M17 7V15" />
                            </svg>
                          )}
                        </span>
                        <span className="block text-sm text-ink-500 mt-0.5 leading-snug">
                          {item.blurb}
                        </span>
                      </span>
                    </div>
                  );
                  return item.external ? (
                    <a
                      key={item.title}
                      href={item.href}
                      target="_blank"
                      rel="noreferrer"
                      className="block"
                    >
                      {content}
                    </a>
                  ) : (
                    <Link key={item.title} to={item.to!} className="block">
                      {content}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {menu.cta && (
          <div className="mt-6 rounded-2xl overflow-hidden relative">
            <div
              className="absolute inset-0 opacity-90"
              style={{
                background:
                  "linear-gradient(135deg, rgba(91,63,228,0.12) 0%, rgba(38,168,162,0.15) 100%)",
              }}
            />
            <Link
              to={menu.cta.to}
              className="relative flex items-center gap-3 px-5 py-4 group"
            >
              <div className="flex-1">
                <div className="font-semibold text-ink-900">
                  {menu.cta.label}
                </div>
                <div className="text-sm text-ink-500">{menu.cta.blurb}</div>
              </div>
              <span className="font-mono text-xs text-royal">→</span>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Navbar() {
  const [aboutOpen, setAboutOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();

  useEffect(() => {
    setAboutOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setAboutOpen(false);
        setMobileOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setAboutOpen(false), 180);
  };
  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  return (
    <header className="sticky top-0 z-40">
      <div
        className="absolute inset-x-0 top-0 h-20 pointer-events-none -z-10"
        style={{
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.85), rgba(255,255,255,0))",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
        }}
      />
      <div className="container-xl">
        <nav className="mt-3 sm:mt-4 glass flex items-center gap-2 px-3 sm:px-4 py-2">
          <Link
            to="/"
            className="flex items-center group shrink-0"
            aria-label="Sanctology — home"
          >
            <img
              src="/sanctology-wordmark.svg"
              alt="Sanctology — Sanctuary of Hope"
              className="h-8 sm:h-9 w-auto"
            />
          </Link>

          {/* ========== DESKTOP NAV ========== */}
          <div className="hidden lg:flex items-center gap-1 relative flex-1 justify-center">
            {/* Direct top-level tabs — Elder Sanctuary + Orphan Sanctuary first, most prominent */}
            {DIRECT_LINKS.slice(0, 2).map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `group rounded-full px-4 py-1.5 text-sm font-medium transition ${
                    isActive
                      ? "bg-white/80 text-royal shadow-xs"
                      : "text-ink-800 hover:text-royal hover:bg-white/60"
                  }`
                }
              >
                <span className="block leading-tight">{l.label}</span>
                {l.sub && (
                  <span className="block text-[10px] font-mono uppercase tracking-[0.15em] text-ink-400 group-hover:text-royal/70 leading-tight">
                    {l.sub}
                  </span>
                )}
              </NavLink>
            ))}

            {/* Volunteer + Contact */}
            {DIRECT_LINKS.slice(2).map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `rounded-full px-4 py-2 text-sm font-medium transition ${
                    isActive
                      ? "bg-white/80 text-royal shadow-xs"
                      : "text-ink-700 hover:text-royal"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}

            {/* About mega menu */}
            <div
              className="relative"
              onMouseEnter={() => {
                cancelClose();
                setAboutOpen(true);
              }}
              onMouseLeave={scheduleClose}
            >
              <button
                type="button"
                onClick={() => setAboutOpen((v) => !v)}
                aria-expanded={aboutOpen}
                className={`rounded-full px-4 py-2 text-sm font-medium transition ${
                  aboutOpen
                    ? "bg-white/80 text-royal shadow-xs"
                    : "text-ink-700 hover:text-royal"
                }`}
              >
                <span className="inline-flex items-center gap-1.5">
                  {ABOUT_MENU.label}
                  <svg
                    className={`w-3 h-3 transition ${aboutOpen ? "rotate-180" : ""}`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </span>
              </button>
              {aboutOpen && <MegaPanel menu={ABOUT_MENU} />}
            </div>
          </div>

          <div className="flex items-center gap-2 ml-auto lg:ml-0 shrink-0">
            <Link
              to="/elder-sanctuary"
              className="hidden sm:inline-flex btn-primary text-sm py-2 px-4"
            >
              Join a waitlist
            </Link>
            <button
              type="button"
              aria-label="Toggle menu"
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden rounded-full p-2 text-ink-800 hover:bg-white/70"
            >
              <svg
                viewBox="0 0 24 24"
                width="22"
                height="22"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                {mobileOpen ? (
                  <>
                    <line x1="6" y1="6" x2="18" y2="18" />
                    <line x1="6" y1="18" x2="18" y2="6" />
                  </>
                ) : (
                  <>
                    <line x1="4" y1="7" x2="20" y2="7" />
                    <line x1="4" y1="12" x2="20" y2="12" />
                    <line x1="4" y1="17" x2="20" y2="17" />
                  </>
                )}
              </svg>
            </button>
          </div>
        </nav>
      </div>

      {/* ========== MOBILE NAV ========== */}
      {mobileOpen && (
        <div className="lg:hidden container-xl mt-2">
          <div
            className="rounded-3xl p-4 space-y-4 animate-fadeUp border border-white"
            style={{
              background: "rgba(255,255,255,0.97)",
              backdropFilter: "blur(24px)",
              WebkitBackdropFilter: "blur(24px)",
              boxShadow: "0 28px 60px -24px rgba(91,63,228,0.28)",
            }}
          >
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-500 mb-2 px-2">
                Our programs
              </div>
              <div className="space-y-1">
                <Link
                  to="/elder-sanctuary"
                  className="flex items-center gap-3 rounded-xl px-3 py-3 hover:bg-white/70 transition"
                >
                  <span className="h-2 w-2 rounded-full bg-peach-glow ring-4 ring-white shadow-sm" />
                  <span>
                    <span className="block font-semibold text-ink-900">
                      Elder Sanctuary
                    </span>
                    <span className="block text-xs text-ink-500">
                      Homeless elders · waitlist open
                    </span>
                  </span>
                </Link>
                <Link
                  to="/orphan-sanctuary"
                  className="flex items-center gap-3 rounded-xl px-3 py-3 hover:bg-white/70 transition"
                >
                  <span className="h-2 w-2 rounded-full bg-sky-glow ring-4 ring-white shadow-sm" />
                  <span>
                    <span className="block font-semibold text-ink-900">
                      Orphan Sanctuary
                    </span>
                    <span className="block text-xs text-ink-500">
                      Orphan children · waitlist open
                    </span>
                  </span>
                </Link>
                <Link
                  to="/volunteer"
                  className="flex items-center gap-3 rounded-xl px-3 py-3 hover:bg-white/70 transition"
                >
                  <span className="h-2 w-2 rounded-full bg-mint-glow ring-4 ring-white shadow-sm" />
                  <span>
                    <span className="block font-semibold text-ink-900">
                      Volunteer
                    </span>
                    <span className="block text-xs text-ink-500">
                      Give time, remote or in person
                    </span>
                  </span>
                </Link>
              </div>
            </div>

            {ABOUT_MENU.sections.map((s) => (
              <div key={s.heading}>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-500 mb-2 px-2">
                  {s.heading}
                </div>
                <div className="space-y-1">
                  {s.items.map((item) => {
                    const content = (
                      <div className="flex gap-3 items-start rounded-xl px-3 py-2.5 hover:bg-white/70 transition">
                        <span className="mt-1.5">
                          <AccentDot accent={item.accent} />
                        </span>
                        <span className="flex-1">
                          <span className="block font-semibold text-ink-900 text-sm">
                            {item.title}
                            {item.external && " ↗"}
                          </span>
                          <span className="block text-xs text-ink-500 mt-0.5 leading-snug">
                            {item.blurb}
                          </span>
                        </span>
                      </div>
                    );
                    return item.external ? (
                      <a
                        key={item.title}
                        href={item.href}
                        target="_blank"
                        rel="noreferrer"
                        className="block"
                      >
                        {content}
                      </a>
                    ) : (
                      <Link key={item.title} to={item.to!} className="block">
                        {content}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}

            <div className="border-t border-ink-200/50 pt-3 space-y-1">
              <Link
                to="/contact"
                className="block rounded-xl px-3 py-2 text-ink-800 font-medium hover:bg-white/70"
              >
                Contact
              </Link>
            </div>
            <Link to="/elder-sanctuary" className="btn-primary w-full">
              Join a waitlist
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
